import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.jsx';
import { deleteScholar, bibliographyLinkFor } from '../../lib/scholars.js';

export default function ScholarDetailModal({ scholar, onClose, onDeleted }) {
  const { t } = useTranslation();
  const { isAdmin, isSignedIn, user } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!scholar) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [scholar, onClose]);

  if (!scholar) return null;

  const canEdit = isAdmin || (user && scholar.profile_id === user.id);
  const bibLink = bibliographyLinkFor(scholar);
  const location = [scholar.city, scholar.country].filter(Boolean).join(', ');

  async function handleDelete() {
    setBusy(true);
    setError(null);
    try {
      await deleteScholar(scholar.id);
      onClose();
      onDeleted?.(scholar.id);
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }

  function handleEdit() {
    onClose();
    navigate(`/scholars/${scholar.id}/edit`);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-navy-deep/80 p-4 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="relative my-12 w-full max-w-2xl rounded-xl border border-white/10 bg-navy-base/95 p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full border border-white/10 bg-navy-mid/40 px-3 py-1 text-sm text-ink-secondary hover:border-gold/40 hover:text-gold-light"
          aria-label={t('common.cancel')}
        >
          ×
        </button>

        <h2 className="font-serif text-3xl font-semibold leading-snug text-cream">
          {scholar.name}
        </h2>
        {scholar.position && (
          <p className="mt-1 text-base text-ink-secondary">{scholar.position}</p>
        )}

        <dl className="mt-6 grid grid-cols-1 gap-y-3 text-sm md:grid-cols-[140px_1fr]">
          <Field label={t('scholars.detail.department')} value={scholar.department} />
          <Field label={t('scholars.detail.university')} value={scholar.university} />
          <Field label={t('scholars.detail.location')} value={location} />
          {isSignedIn && (
            <Field label={t('scholars.detail.email')} value={scholar.email_public} email />
          )}
          <Field label={t('scholars.detail.website')} value={scholar.website} link />
        </dl>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {bibLink && (
            <Link to={bibLink} className="btn-primary text-sm" onClick={onClose}>
              {t('scholars.detail.viewBibliography')}
            </Link>
          )}
          {scholar.website && (
            <a
              href={scholar.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm"
            >
              {t('scholars.detail.visitWebsite')}
            </a>
          )}
        </div>

        {canEdit && (
          <footer className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-white/5 pt-6">
            {error && <p className="mr-auto text-sm text-red-300">{error}</p>}
            {confirmDelete ? (
              <>
                <span className="text-sm text-ink-secondary">{t('scholars.detail.confirmDelete')}</span>
                <button type="button" className="btn-ghost text-sm" onClick={() => setConfirmDelete(false)} disabled={busy}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-red-500/50 bg-red-950/30 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  {busy ? t('common.loading') : t('common.delete')}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn-ghost text-sm" onClick={handleEdit}>
                  {t('scholars.detail.editProfile')}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="rounded-md border border-red-500/40 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                    onClick={() => setConfirmDelete(true)}
                  >
                    {t('common.delete')}
                  </button>
                )}
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, link, email }) {
  if (!value) return null;
  let display = value;
  if (link) {
    display = (
      <a href={value} target="_blank" rel="noopener noreferrer" className="text-gold-light hover:underline">
        {value}
      </a>
    );
  } else if (email) {
    display = (
      <a href={`mailto:${value}`} className="text-gold-light hover:underline">
        {value}
      </a>
    );
  }
  return (
    <>
      <dt className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">
        {label}
      </dt>
      <dd className="text-ink-primary">{display}</dd>
    </>
  );
}
